/**
 * Closing Deadline Utilities
 *
 * The monthly closing deadline is midnight (00:00) of the 1st business day
 * of each month. Business days skip weekends AND Brazilian national,
 * Espírito Santo state, and Vila Velha municipal holidays.
 *
 * Feriados nacionais podem ser obtidos da Brasil API (fetchFeriadosBrasilApi).
 * Chame inicializarFeriadosBrasilApi() no mount do app para usar dados oficiais.
 */

// ── Cache de feriados (Brasil API) ────────────────────────────────────────
const feriadosApiCache: Record<number, Set<string>> = {};

/**
 * Popula o cache de feriados nacionais com dados da Brasil API.
 * Chamar no mount do app (ex: useEffect no Layout).
 */
export async function inicializarFeriadosBrasilApi(anos: number[]): Promise<void> {
  const { fetchFeriadosSetBrasilApi } = await import("@/shared/lib/brasilApi");
  for (const ano of anos) {
    const set = await fetchFeriadosSetBrasilApi(ano);
    if (set.size > 0) feriadosApiCache[ano] = set;
  }
}

// ── Holiday helpers ──────────────────────────────────────────────────────

/** Easter date via anonymous Gregorian algorithm */
function getEasterDate(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Returns a Set of holiday date-keys for a given year.
 * Usa cache da Brasil API se disponível; senão, feriados hardcoded.
 * Sempre inclui: ES state e Vila Velha municipal holidays.
 */
function getHolidays(year: number): Set<string> {
  const holidays = new Set<string>();

  const add = (m: number, d: number) => holidays.add(dateKey(new Date(year, m - 1, d)));

  // ── Feriados Nacionais: API ou hardcoded ──
  const fromApi = feriadosApiCache[year];
  if (fromApi && fromApi.size > 0) {
    fromApi.forEach((k) => holidays.add(k));
  } else {
    add(1, 1);
    add(4, 21);
    add(5, 1);
    add(9, 7);
    add(10, 12);
    add(11, 2);
    add(11, 15);
    add(11, 20);
    add(12, 25);
    const easter = getEasterDate(year);
    holidays.add(dateKey(addDays(easter, -49)));
    holidays.add(dateKey(addDays(easter, -48)));
    holidays.add(dateKey(addDays(easter, -47)));
    holidays.add(dateKey(addDays(easter, -46)));
    holidays.add(dateKey(addDays(easter, -2)));
    holidays.add(dateKey(easter));
    holidays.add(dateKey(addDays(easter, 60)));
    add(12, 24);
    add(12, 31);
  }

  // ── Feriado Estadual do Espírito Santo ──
  add(10, 28); // Dia do Servidor Público (ES)

  // ── Feriados Municipais de Vila Velha ──
  add(5, 23);  // Aniversário de Vila Velha
  add(9, 8);   // Nossa Senhora da Penha (padroeira)

  return holidays;
}

function isHoliday(date: Date): boolean {
  const holidays = getHolidays(date.getFullYear());
  return holidays.has(dateKey(date));
}

function isBusinessDay(date: Date): boolean {
  const dow = date.getDay();
  if (dow === 0 || dow === 6) return false; // weekend
  if (isHoliday(date)) return false;
  return true;
}

// ── Fuso Horário Brasília ─────────────────────────────────────────────────

/**
 * Retorna a data/hora ATUAL no fuso horário de Brasília (America/Sao_Paulo).
 * Servidores rodam em UTC; sem essa correção o prazo bloqueia 3h antes no Brasil.
 */
function nowInBrasilia(): Date {
  const formatter = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type: string) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? "0");
  // Constrói um Date "local" com os valores do fuso de Brasília
  return new Date(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
}

// ── Public API ───────────────────────────────────────────────────────────

/**
 * Returns the 1st business day of month/year (skips weekends + holidays).
 */
export function getFirstBusinessDay(year: number, month: number): Date {
  let d = new Date(year, month - 1, 1);
  while (!isBusinessDay(d)) {
    d = addDays(d, 1);
  }
  // Set to 00:00:00
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * For a reference month "YYYY-MM", returns the deadline: midnight of the
 * 1st business day of the NEXT month.
 */
export function getClosingDeadline(referenceMonth: string): Date {
  const [year, month] = referenceMonth.split("-").map(Number);
  // Next month
  let nextMonth = month + 1;
  let nextYear = year;
  if (nextMonth > 12) {
    nextMonth = 1;
    nextYear++;
  }
  return getFirstBusinessDay(nextYear, nextMonth);
}

/**
 * Returns true if the deadline for a given reference month has already passed.
 */
export function isDeadlineExpired(referenceMonth: string): boolean {
  const deadline = getClosingDeadline(referenceMonth);
  return nowInBrasilia() >= deadline;
}

/**
 * Returns info for the global alert banner.
 * Shows alert when 3 or fewer days remain before the next closing deadline.
 */
export function getClosingAlertInfo(): {
  show: boolean;
  deadline: Date;
  daysLeft: number;
  hoursLeft: number;
  referenceMonth: string;
} {
  const now = nowInBrasilia();
  // The reference month is the current month
  const refMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const deadline = getClosingDeadline(refMonth);

  const diffMs = deadline.getTime() - now.getTime();
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const hoursLeft = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));

  return {
    show: daysLeft <= 3 && daysLeft >= 0,
    deadline,
    daysLeft: Math.max(0, daysLeft),
    hoursLeft,
    referenceMonth: refMonth,
  };
}

/**
 * Format reference month label: "2025-01" → "Janeiro 2025"
 */
export function formatRefMonth(refMonth: string): string {
  const MONTHS = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  ];
  const [y, m] = refMonth.split("-");
  return `${MONTHS[parseInt(m) - 1]} ${y}`;
}

/**
 * Competência `YYYY-MM` estritamente posterior ao mês civil de `end_date`
 * (contrato já encerrado para essa competência). `end_date` ausente → false.
 */
export function referenceMonthAfterContractEnd(
  referenceMonth: string,
  endDate: string | null | undefined,
): boolean {
  if (!endDate || endDate.length < 7) return false;
  const endYm = endDate.slice(0, 7);
  return referenceMonth > endYm;
}
